import { Box, Card, Flex } from '@chakra-ui/react';
import VAlert from '@voly/ui-components/src/v2/VAlert.jsx';
import VButton from '@voly/ui-components/src/v2/VButton.jsx';
import VCheckbox from '@voly/ui-components/src/v2/VCheckbox';
import VStack from '@voly/ui-components/src/v2/VStack';
import VStackRow from '@voly/ui-components/src/v2/VStackRow';
import VText from '@voly/ui-components/src/v2/VText.jsx';
import { translate } from 'src/app/project/features/translate/translate';
import { isGroup, TLayoutEntry, TLayoutGroup } from '../organisms/CustomFieldGroupsSettings';
import { TExcludedFields } from '../organisms/ImportCustomFieldGroups';
import ImportCustomFieldRow from './ImportCustomFieldRow';
import { TSiteOption } from './SelectImportSite';
import VMaterialIcon from '@voly/ui-components/src/v2/icons/VMaterialIcon.jsx';

const getGroupCheckedState = (
  selectableFieldIds: string[],
  selectedFieldIds: string[],
): boolean | 'indeterminate' => {
  const selectedCount = selectableFieldIds.filter((fieldId) =>
    selectedFieldIds.includes(fieldId),
  ).length;

  if (selectedCount === 0) {
    return false;
  }

  if (selectedCount === selectableFieldIds.length) {
    return true;
  }

  return 'indeterminate';
};

export interface SelectImportGroupsProps {
  site: TSiteOption;
  layout: TLayoutEntry[];
  selectableFieldIds: string[];
  excludedFields: TExcludedFields;
  selectedFieldIds: string[];
  selectionExcludesFields: boolean;
  onToggleField: (fieldId: string) => void;
  onToggleGroup: (group: TLayoutGroup) => void;
  onToggleAll: () => void;
  onBackToSiteSelection: (accountId: string) => void;
}

const SelectImportGroups = ({
  site,
  layout,
  selectableFieldIds,
  excludedFields,
  selectedFieldIds,
  selectionExcludesFields,
  onToggleField,
  onToggleGroup,
  onToggleAll,
  onBackToSiteSelection,
}: SelectImportGroupsProps) => {
  const allSelected =
    selectableFieldIds.length > 0 &&
    selectableFieldIds.every((fieldId) => selectedFieldIds.includes(fieldId));

  return (
    <VStack gap={6}>
      <VStackRow>
        <VButton color="tertiary" onClick={() => onBackToSiteSelection(site.accountId)}>
          <VMaterialIcon name="chevronLeft" />

          {translate('back_to_site_selection')}
        </VButton>
      </VStackRow>

      <VStackRow justifyContent={'space-between'}>
        <VStack>
          <VText fontWeight="bold" textStyle="lg">
            {site.label}
          </VText>

          <VText color="fg.muted">{site.accountName}</VText>
        </VStack>

        <Flex alignItems={'flex-end'}>
          <VButton color="tertiary" onClick={onToggleAll}>
            {allSelected ? translate('deselect_all') : translate('select_all')}
          </VButton>
        </Flex>
      </VStackRow>

      <VStack>
        {layout.map((entry) => {
          if (!isGroup(entry)) {
            const fieldSelected = selectedFieldIds.includes(entry.id);

            return (
              <Card.Root
                key={entry.id}
                borderRadius="sm"
                overflow="hidden"
                borderColor={fieldSelected ? 'primary.solid' : 'border'}
              >
                <Card.Body p={0}>
                  <ImportCustomFieldRow
                    field={entry}
                    selected={fieldSelected}
                    exclusionReason={excludedFields[entry.id]}
                    onToggle={onToggleField}
                  />
                </Card.Body>
              </Card.Root>
            );
          }

          const groupSelectableFieldIds = entry.items
            .map((field) => field.id)
            .filter((fieldId) => selectableFieldIds.includes(fieldId));
          const groupCheckedState = getGroupCheckedState(groupSelectableFieldIds, selectedFieldIds);

          return (
            <Card.Root
              key={entry.id}
              borderRadius="sm"
              overflow="hidden"
              borderColor={groupCheckedState ? 'primary.solid' : 'border'}
            >
              <Card.Body p={0}>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  background={groupCheckedState ? 'primary.subtle' : 'bg.muted'}
                >
                  <VCheckbox
                    checked={groupCheckedState}
                    disabled={groupSelectableFieldIds.length === 0}
                    onCheckedChange={() => onToggleGroup(entry)}
                  >
                    <VText fontWeight="bold">{entry.title}</VText>
                  </VCheckbox>
                </Box>

                {entry.items.map((field) => (
                  <ImportCustomFieldRow
                    key={field.id}
                    field={field}
                    selected={selectedFieldIds.includes(field.id)}
                    exclusionReason={excludedFields[field.id]}
                    onToggle={onToggleField}
                  />
                ))}
              </Card.Body>
            </Card.Root>
          );
        })}
      </VStack>

      {selectionExcludesFields && (
        <VAlert
          status="warning"
          variant="surface"
          title={translate('import_custom_fields_some_excluded')}
          description={undefined}
          icon={undefined}
        />
      )}
    </VStack>
  );
};

export default SelectImportGroups;
